import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import styles from './Popover.module.scss';

// ============================================
// TYPES
// ============================================

export type PopoverPlacement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'right';

export type PopoverTriggerMode = 'hover' | 'click';

export interface PopoverProps extends Omit<HTMLAttributes<HTMLDivElement>, 'content'> {
  /** Content rendered inside the floating panel */
  content: ReactNode;
  /** The trigger element */
  children: ReactNode;
  /** Where to place the panel relative to the trigger */
  placement?: PopoverPlacement;
  /** How to open the popover */
  trigger?: PopoverTriggerMode;
  /** Controlled open state (overrides internal state) */
  open?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Delay in ms before showing on hover */
  openDelay?: number;
  /** Delay in ms before hiding on hover */
  closeDelay?: number;
  /** Whether the popover is disabled */
  disabled?: boolean;
}

// ============================================
// HELPERS
// ============================================

const PLACEMENT_CLASS: Record<PopoverPlacement, string | undefined> = {
  top: styles.top,
  'top-start': styles.topStart,
  'top-end': styles.topEnd,
  bottom: styles.bottom,
  'bottom-start': styles.bottomStart,
  'bottom-end': styles.bottomEnd,
  left: styles.left,
  right: styles.right,
};

// ============================================
// COMPONENT
// ============================================

const Popover = forwardRef<HTMLDivElement, PopoverProps>(
  (
    {
      content,
      children,
      placement = 'bottom',
      trigger = 'hover',
      open: controlledOpen,
      onOpenChange,
      openDelay = 200,
      closeDelay = 150,
      disabled = false,
      className,
      ...rest
    },
    ref,
  ) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = controlledOpen !== undefined;
    const isOpen = isControlled ? controlledOpen : internalOpen;

    const openTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const setOpen = useCallback(
      (value: boolean) => {
        if (!isControlled) setInternalOpen(value);
        onOpenChange?.(value);
      },
      [isControlled, onOpenChange],
    );

    const clearTimers = useCallback(() => {
      clearTimeout(openTimerRef.current);
      clearTimeout(closeTimerRef.current);
    }, []);

    // --- Hover handlers ---
    const handleMouseEnter = useCallback(() => {
      if (disabled || trigger !== 'hover') return;
      clearTimers();
      openTimerRef.current = setTimeout(() => setOpen(true), openDelay);
    }, [disabled, trigger, clearTimers, setOpen, openDelay]);

    const handleMouseLeave = useCallback(() => {
      if (disabled || trigger !== 'hover') return;
      clearTimers();
      closeTimerRef.current = setTimeout(() => setOpen(false), closeDelay);
    }, [disabled, trigger, clearTimers, setOpen, closeDelay]);

    // --- Click handler ---
    const handleClick = useCallback(() => {
      if (disabled || trigger !== 'click') return;
      setOpen(!isOpen);
    }, [disabled, trigger, isOpen, setOpen]);

    // --- Close on outside click (click mode) ---
    useEffect(() => {
      if (trigger !== 'click' || !isOpen) return;

      const handleOutsideClick = (e: MouseEvent) => {
        if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
          setOpen(false);
        }
      };

      document.addEventListener('mousedown', handleOutsideClick);
      return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [trigger, isOpen, setOpen]);

    // --- Close on Escape ---
    useEffect(() => {
      if (!isOpen) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setOpen(false);
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, setOpen]);

    // --- Cleanup timers on unmount ---
    useEffect(() => clearTimers, [clearTimers]);

    const placementClass = PLACEMENT_CLASS[placement] ?? styles.bottom;

    return (
      <div
        ref={(node) => {
          (wrapperRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }}
        className={[styles.wrapper, className].filter(Boolean).join(' ')}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...rest}
      >
        {/* Trigger */}
        <span
          className={styles.trigger}
          onClick={handleClick}
          role={trigger === 'click' ? 'button' : undefined}
          tabIndex={trigger === 'click' ? 0 : undefined}
          aria-expanded={trigger === 'click' ? isOpen : undefined}
          onKeyDown={
            trigger === 'click'
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClick();
                  }
                }
              : undefined
          }
        >
          {children}
        </span>

        {/* Panel */}
        <div
          className={[styles.panel, placementClass, isOpen ? styles.open : '']
            .filter(Boolean)
            .join(' ')}
          role="tooltip"
          aria-hidden={!isOpen}
        >
          {content}
        </div>
      </div>
    );
  },
);

Popover.displayName = 'Popover';

export default Popover;
